import { ref, computed } from 'vue'

export interface CookiePreferences {
  essential: boolean
  analytics: boolean
  marketing: boolean
  functional: boolean
}

export interface ConsentData {
  timestamp: string
  version: string
  preferences: CookiePreferences
}

const COOKIE_CONSENT_KEY = 'fodboldpakker_cookie_consent'
const CONSENT_VERSION = '1.0'

// Global consent state
const consentGiven = ref(false)
const preferences = ref<CookiePreferences>({
  essential: true,
  analytics: false,
  marketing: false,
  functional: false
})

export const useConsentManagement = () => {
  /**
   * Get stored consent from localStorage
   */
    const getStoredConsent = (): ConsentData | null => {
    if (typeof window === 'undefined') return null

    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }

  /**
   * Store consent in localStorage
   */
    const storeConsent = (consentData: ConsentData): void => {
    if (typeof window === 'undefined') return

    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData))
    } catch {
      // Silently fail if localStorage is not available
    }
  }

    /**
   * Apply consent settings to Google Analytics and Meta Pixel
   */
  const applyConsentSettings = (consentPreferences: CookiePreferences): void => {
    if (typeof window === 'undefined') return

    // Update Meta Pixel consent
    if (window.fbq) {
      if (consentPreferences.marketing) {
        window.fbq('consent', 'grant')
      } else {
        window.fbq('consent', 'revoke')
      }
    }

    // Dispatch event for analytics plugin
    const hasAnyConsent = consentPreferences.analytics || consentPreferences.marketing

    document.dispatchEvent(new CustomEvent('cookieyes_consent_update', {
      detail: {
        accepted: hasAnyConsent,
        preferences: consentPreferences
      }
    }))
  }

  /**
   * Save consent preferences
   */
  const saveConsent = (consentPreferences: CookiePreferences): void => {
    const consentData: ConsentData = {
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
      preferences: consentPreferences
    }

    // Update reactive state
    preferences.value = { ...consentPreferences }
    consentGiven.value = true

    // Store in localStorage
    storeConsent(consentData)

    // Apply to tracking services
    applyConsentSettings(consentPreferences)
  }

  /**
   * Accept all cookies
   */
  const acceptAll = (): void => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    }
    saveConsent(allAccepted)
  }

  /**
   * Reject all non-essential cookies
   */
  const rejectAll = (): void => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    }
    saveConsent(onlyEssential)
  }

  /**
   * Check if consent has been given
   */
  const hasConsent = computed(() => consentGiven.value)

  /**
   * Check if specific consent type is granted
   */
  const hasAnalyticsConsent = computed(() => preferences.value.analytics)
  const hasMarketingConsent = computed(() => preferences.value.marketing)
  const hasFunctionalConsent = computed(() => preferences.value.functional)

    /**
   * Initialize consent from stored data
   */
    const initializeConsent = (): boolean => {
    const storedConsent = getStoredConsent()

    if (storedConsent?.version === CONSENT_VERSION) {
      preferences.value = { ...preferences.value, ...storedConsent.preferences }
      consentGiven.value = true
      applyConsentSettings(storedConsent.preferences)
      return true
    }

    return false
  }

  /**
   * Revoke consent (GDPR right to withdraw)
   */
    const revokeConsent = (): void => {
    if (typeof window === 'undefined') return

    localStorage.removeItem(COOKIE_CONSENT_KEY)
    const deniedPreferences = { essential: true, analytics: false, marketing: false, functional: false }
    preferences.value = deniedPreferences
    consentGiven.value = false
    applyConsentSettings(deniedPreferences)
  }

    return {
    // State
    preferences: readonly(preferences),
    hasConsent,
    hasAnalyticsConsent,
    hasMarketingConsent,
    hasFunctionalConsent,
    // Actions
    acceptAll,
    rejectAll,
    saveConsent,
    initializeConsent,
    revokeConsent,
    getStoredConsent,
    applyConsentSettings
  }
}
